import { useState } from "react";
import {
  SearchIcon,
  VideoIcon,
  FileQuestionIcon,
  LightbulbIcon,
  UsersIcon,
  ClipboardListIcon,
  FileTextIcon,
} from "lucide-react";
import {
  Dialog,
  DialogContent,
  DialogHeader,
  DialogTitle,
  DialogDescription,
} from "@/components/ui/dialog";
import { Input } from "@/components/ui/input";

export type ContentType = "Video" | "Quiz" | "BrainstormingSession" | "MeetupSession" | "Activity" | "Document";

export interface ContentItem {
  id: number;
  title: string;
  type: ContentType;
  description?: string;
  activityType?: string;
  duration?: string;
  questionsCount?: number;
  scheduledDate?: string;
  weekNumber?: number;
  durationMinutes?: number;
  fileCount?: number;
}

interface ContentPickerDialogProps {
  open: boolean;
  onClose: () => void;
  onSelect: (item: ContentItem) => void;
  items: ContentItem[];
  alreadyAddedIds: number[];
  contentType: ContentType;
}

export function ContentPickerDialog({
  open,
  onClose,
  onSelect,
  items,
  alreadyAddedIds,
  contentType,
}: ContentPickerDialogProps) {
  const [search, setSearch] = useState("");

  const filtered = items.filter((item) =>
    item.title.toLowerCase().includes(search.toLowerCase())
  );

  const handleSelect = (item: ContentItem) => {
    if (alreadyAddedIds.includes(item.id)) return;
    onSelect(item);
    setSearch("");
    onClose();
  };

  return (
    <Dialog open={open} onOpenChange={(isOpen) => !isOpen && onClose()}>
      <DialogContent className="max-w-lg max-h-[80vh] flex flex-col">
        <DialogHeader>
          <DialogTitle>
            Add{" "}
            {contentType === "Video"
              ? "Video"
              : contentType === "Quiz"
                ? "Quiz"
                : contentType === "MeetupSession"
                  ? "Meetup Session"
                  : contentType === "Activity"
                    ? "Activity"
                    : contentType === "Document"
                      ? "Document"
                      : "Brainstorming Session"}
          </DialogTitle>
          <DialogDescription>
            {contentType === "BrainstormingSession"
              ? "Select a scheduled brainstorming session"
              : contentType === "MeetupSession"
                ? "Select a scheduled meetup session"
                : contentType === "Activity"
                  ? "Select a published activity"
                  : contentType === "Document"
                    ? "Select a published document"
                    : `Search and select a ${contentType.toLowerCase()} from the library`}
          </DialogDescription>
        </DialogHeader>

        <div className="relative">
          <SearchIcon className="absolute left-3 top-1/2 -translate-y-1/2 h-4 w-4 text-muted-foreground" />
          <Input
            value={search}
            onChange={(e) => setSearch(e.target.value)}
            placeholder={
              contentType === "BrainstormingSession"
                ? "Search brainstorming sessions..."
                : contentType === "MeetupSession"
                  ? "Search meetup sessions..."
                  : contentType === "Activity"
                    ? "Search activities..."
                    : contentType === "Document"
                      ? "Search documents..."
                      : `Search ${contentType.toLowerCase()}s...`
            }
            className="pl-9"
          />
        </div>

        <div className="flex-1 overflow-y-auto min-h-0 max-h-[400px] space-y-1">
          {filtered.length === 0 ? (
            <p className="text-sm text-muted-foreground text-center py-8">
              No{" "}
              {contentType === "BrainstormingSession"
                ? "brainstorming sessions"
                : contentType === "MeetupSession"
                  ? "meetup sessions"
                  : contentType === "Activity"
                    ? "activities"
                    : contentType === "Document"
                      ? "documents"
                      : `${contentType.toLowerCase()}s`}{" "}
              found
            </p>
          ) : (
            filtered.map((item) => {
              const isAdded = alreadyAddedIds.includes(item.id);
              return (
                <button
                  key={item.id}
                  onClick={() => handleSelect(item)}
                  disabled={isAdded}
                  className={`w-full text-left p-3 rounded-md border transition-colors ${
                    isAdded
                      ? "opacity-50 cursor-not-allowed bg-muted border-border"
                      : "hover:bg-accent border-border cursor-pointer"
                  }`}
                >
                  <div className="flex items-start gap-3">
                    <div className="mt-0.5">
                      {contentType === "Video" ? (
                        <VideoIcon className="h-4 w-4 text-blue-500" />
                      ) : contentType === "Quiz" ? (
                        <FileQuestionIcon className="h-4 w-4 text-purple-500" />
                      ) : contentType === "MeetupSession" ? (
                        <UsersIcon className="h-4 w-4 text-green-500" />
                      ) : contentType === "Activity" ? (
                        <ClipboardListIcon className="h-4 w-4 text-emerald-500" />
                      ) : contentType === "Document" ? (
                        <FileTextIcon className="h-4 w-4 text-orange-500" />
                      ) : (
                        <LightbulbIcon className="h-4 w-4 text-amber-500" />
                      )}
                    </div>
                    <div className="flex-1 min-w-0">
                      <p className="text-sm font-medium text-foreground truncate">
                        {item.title}
                      </p>
                      <div className="flex items-center gap-2 mt-0.5">
                        {item.duration && (
                          <span className="text-xs text-muted-foreground">
                            {item.duration}
                          </span>
                        )}
                        {item.questionsCount !== undefined && (
                          <span className="text-xs text-muted-foreground">
                            {item.questionsCount} questions
                          </span>
                        )}
                        {item.weekNumber !== undefined && (
                          <span className="text-xs text-muted-foreground">
                            Week {item.weekNumber}
                          </span>
                        )}
                        {item.scheduledDate && (
                          <span className="text-xs text-muted-foreground">
                            {item.scheduledDate}
                          </span>
                        )}
                        {item.durationMinutes !== undefined && (
                          <span className="text-xs text-muted-foreground">
                            {item.durationMinutes} min
                          </span>
                        )}
                        {item.activityType && (
                          <span className="text-xs text-muted-foreground">
                            {item.activityType}
                          </span>
                        )}
                        {item.fileCount !== undefined && (
                          <span className="text-xs text-muted-foreground">
                            {item.fileCount} file{item.fileCount === 1 ? "" : "s"}
                          </span>
                        )}
                        {isAdded && (
                          <span className="text-xs text-amber-600 font-medium">
                            Already added
                          </span>
                        )}
                      </div>
                    </div>
                  </div>
                </button>
              );
            })
          )}
        </div>
      </DialogContent>
    </Dialog>
  );
}
